import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Shield, Copy, ArrowLeft, Tags, Building2, Percent, Trash2, Send, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import PricingUpdatePanel from "@/components/admin/PricingUpdatePanel";
import MezzoPricingPanel from "@/components/admin/MezzoPricingPanel";
import VeroPricingPanel from "@/components/admin/VeroPricingPanel";
import ISSPricingPanel from "@/components/admin/ISSPricingPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (v) => {
  try {
    return v ? new Date(v).toLocaleString() : "—";
  } catch {
    return String(v || "—");
  }
};

// Ruled 2026-07-18: fixture_runs is a no-TTL archive — test-harness runs
// carry test_artifact=true stamped at creation (never inferred). This card
// lists + purges ONLY tagged debris; production archives are untouchable.
function TestArtifactCard({ token }) {
  const [runs, setRuns] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/fixture-runs/test-artifacts`, {
        headers: { "X-Admin-Token": token },
      });
      setRuns(data.runs || []);
      setMsg("");
    } catch {
      setMsg("Failed to load test artifacts");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/admin/fixture-runs/test-artifacts`, {
        headers: { "X-Admin-Token": token },
      });
      setMsg(`Purged ${data.deleted} tagged run(s)`);
      await load();
    } catch {
      setMsg("Purge failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card p-6" data-testid="test-artifact-card">
      <div className="section-tag mb-3">Test Artifacts (fixture archive)</div>
      <p className="text-sm text-[var(--ink-2)] mb-4">
        Runs archived by the test harness carry <code>test_artifact</code> —
        purging removes ONLY tagged debris. Production archives are never
        touched from here.
      </p>
      {msg && <p className="text-sm mb-2" data-testid="test-artifact-msg">{msg}</p>}
      <div className="flex items-center gap-3 mb-3">
        <span className="font-mono-num text-sm" data-testid="test-artifact-count">
          {runs === null ? "…" : `${runs.length} tagged run(s)`}
        </span>
        <button
          type="button"
          className="btn-primary"
          disabled={busy || !runs || runs.length === 0}
          onClick={purge}
          data-testid="test-artifact-purge-btn"
        >
          Purge tagged
        </button>
      </div>
      {runs && runs.length > 0 && (
        <div className="max-h-48 overflow-y-auto text-xs font-mono-num divide-y divide-[var(--border)]" data-testid="test-artifact-list">
          {runs.map((r) => (
            <div key={r.run_id} className="py-1 flex justify-between gap-2">
              <span className="truncate">{r.run_id}</span>
              <span className="text-[var(--ink-2)] shrink-0">
                {r.substrate || "—"} · {fmtDate(r.created_at)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BrandingAdmin() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [branding, setBranding] = useState(null);
  const [signupCode, setSignupCode] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierTagline, setSupplierTagline] = useState("");
  const [defaultPricingMode, setDefaultPricingMode] = useState("margin");
  const [lpNativeMode, setLpNativeMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef();

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [b, s] = await Promise.all([
          axios.get(`${API}/branding`),
          axios.get(`${API}/admin/signup-code`, { headers: { "X-Admin-Token": token } }),
        ]);
        setBranding(b.data);
        setSupplierName(b.data.supplier_name || "");
        setSupplierTagline(b.data.supplier_tagline || "");
        setDefaultPricingMode(b.data.default_pricing_mode || "margin");
        setLpNativeMode(!!b.data.lp_native_mode);
        setSignupCode(s.data.signup_code);
      } catch (e) {
        setError(
          e.response?.status === 403
            ? "Invalid admin token. Check the token in your URL (?token=...) and try again."
            : "Failed to load. " + (e.response?.data?.detail || e.message)
        );
      }
    })();
  }, [token]);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
        <div className="card p-8 max-w-md w-full">
          <Shield className="w-10 h-10 text-[var(--brand-text)] mb-4" />
          <h1 className="font-heading text-2xl text-[var(--ink)] mb-2">Branding Admin</h1>
          <p className="text-sm text-[var(--ink-2)]">
            This URL requires an admin token. Append <code className="font-mono">?token=YOUR_TOKEN</code> to the URL. The token lives in <code className="font-mono">backend/.env</code> as <code className="font-mono">SUPPLIER_ADMIN_TOKEN</code>.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
        <div className="card p-8 max-w-md w-full">
          <h1 className="font-heading text-2xl text-[var(--danger)] mb-2">Access denied</h1>
          <p className="text-sm text-[var(--ink-2)]">{error}</p>
        </div>
      </div>
    );
  }

  if (!branding) {
    return <div className="p-10 text-center text-[var(--ink-2)]">Loading…</div>;
  }

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload-logo`, fd, {
        headers: { "Content-Type": "multipart/form-data", "X-Admin-Token": token },
      });
      setBranding({ ...branding, supplier_logo_url: data.url });
      toast.success("Supplier logo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveBranding = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/branding`,
        { supplier_name: supplierName, supplier_tagline: supplierTagline },
        { headers: { "X-Admin-Token": token } }
      );
      setBranding(data);
      toast.success("Branding saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const savePricingDefault = async (mode) => {
    setBusy(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/branding`,
        { default_pricing_mode: mode },
        { headers: { "X-Admin-Token": token } }
      );
      setBranding(data);
      setDefaultPricingMode(data.default_pricing_mode || "margin");
      toast.success(`Default pricing set to ${mode}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const logoUrl = branding.supplier_logo_url
    ? `${process.env.REACT_APP_BACKEND_URL}${branding.supplier_logo_url}`
    : null;

  return (
    <div className="min-h-screen bg-[var(--bg-app)]">
      <header className="bg-[var(--bar-bg)] text-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-[var(--brand-text)]" />
            <div>
              <div className="font-heading text-lg">Branding Admin</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                Supplier-only · do not share this URL
              </div>
            </div>
          </div>
          <Link to="/" className="text-white/70 hover:text-white text-sm">
            <ArrowLeft className="w-4 h-4 inline" /> Back to app
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Signup code */}
        <div className="card p-6" data-testid="signup-code-card">
          <div className="section-tag mb-3">Contractor Access Code</div>
          <p className="text-sm text-[var(--ink-2)] mb-3">
            Give this code to any contractor you want to grant access. They&apos;ll enter it when creating a new account.
          </p>
          <div className="flex items-stretch gap-2">
            <div className="flex-1 bg-[var(--bar-bg)] text-[var(--brand)] font-mono-num text-2xl tracking-[0.3em] px-5 flex items-center">
              {signupCode}
            </div>
            <button
              className="btn-primary"
              onClick={() => {
                navigator.clipboard.writeText(signupCode);
                toast.success("Copied");
              }}
            >
              <Copy className="w-4 h-4" /> Copy
            </button>
          </div>
          <p className="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-3">
            To rotate: change SIGNUP_CODE in <span className="font-mono-num">backend/.env</span> &amp; restart backend.
          </p>
        </div>

        {/* Invite a contractor by email */}
        <InviteContractorPanel token={token} signupCode={signupCode} />

        {/* Supplier brand */}
        <div className="card p-6">
          <div className="section-tag mb-3">Supplier Name &amp; Tagline</div>
          <div className="space-y-4">
            <div>
              <label className="label">Supplier name</label>
              <input
                className="input"
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                data-testid="supplier-name-input"
              />
            </div>
            <div>
              <label className="label">Tagline (sales contact / phone)</label>
              <input
                className="input"
                value={supplierTagline}
                onChange={(e) => setSupplierTagline(e.target.value)}
                data-testid="supplier-tagline-input"
              />
            </div>
            <button className="btn-primary" onClick={saveBranding} disabled={busy} data-testid="save-branding-btn">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {/* Default pricing mode */}
        <div className="card p-6" data-testid="default-pricing-card">
          <div className="flex items-center gap-3 mb-3">
            <Percent className="w-5 h-5 text-[var(--brand-text)]" />
            <div className="section-tag">Default Pricing Mode</div>
          </div>
          <p className="text-sm text-[var(--ink-2)] mb-4">
            Sets how new estimates calculate sell price for every contractor by default.
            Each contractor can still toggle per-estimate, but new estimates start in this mode.
          </p>
          <div
            className="inline-flex border border-[var(--border)] rounded-sm overflow-hidden text-sm font-bold uppercase tracking-wider"
            data-testid="default-pricing-toggle"
          >
            <button
              type="button"
              disabled={busy}
              className={`px-5 py-2 transition ${
                defaultPricingMode === "margin"
                  ? "bg-[var(--bar-bg)] text-white"
                  : "bg-[var(--surface)] text-[var(--ink-2)] hover:bg-[var(--bg-app)]"
              }`}
              onClick={() => savePricingDefault("margin")}
              data-testid="default-pricing-margin"
            >
              Margin
            </button>
            <button
              type="button"
              disabled={busy}
              className={`px-5 py-2 transition border-l border-[var(--border)] ${
                defaultPricingMode === "markup"
                  ? "bg-[var(--bar-bg)] text-white"
                  : "bg-[var(--surface)] text-[var(--ink-2)] hover:bg-[var(--bg-app)]"
              }`}
              onClick={() => savePricingDefault("markup")}
              data-testid="default-pricing-markup"
            >
              Markup
            </button>
          </div>
          <div className="mt-3 text-[11px] text-[var(--muted)] font-mono-num">
            {defaultPricingMode === "margin"
              ? "Margin: sell = base ÷ (1 − %)  — 30% gives a ×1.429 multiplier"
              : "Markup: sell = base × (1 + %)  — 30% gives a ×1.300 multiplier"}
          </div>
        </div>

        {/* LP-native demo mode */}
        <div className="card p-6" data-testid="lp-native-mode-card">
          <div className="section-tag mb-3">LP-Native Mode</div>
          <p className="text-sm text-[var(--ink-2)] mb-4">
            Filters ALL contractor surfaces to the LP SmartSide domain — pickers,
            catalog, tabs. Other domains are hidden, not deleted (presentation-layer
            only). The September demo runs with this ON.
          </p>
          <button
            type="button"
            disabled={busy}
            className={`px-5 py-2 rounded-sm text-sm font-bold uppercase tracking-wider transition ${
              lpNativeMode
                ? "bg-[var(--bar-bg)] text-white"
                : "bg-[var(--surface)] text-[var(--ink-2)] border border-[var(--border)] hover:bg-[var(--bg-app)]"
            }`}
            onClick={async () => {
              setBusy(true);
              try {
                const { data } = await axios.put(
                  `${API}/admin/lp-native-mode`,
                  { enabled: !lpNativeMode },
                  { headers: { "X-Admin-Token": token } }
                );
                setLpNativeMode(!!data.enabled);
              } catch (e) {
                setError("Failed to toggle LP-native mode");
              } finally {
                setBusy(false);
              }
            }}
            data-testid="lp-native-mode-toggle"
          >
            {lpNativeMode ? "ON — LP domain only" : "OFF — all domains visible"}
          </button>
        </div>

        {/* Test-artifact purge (ruled 2026-07-18) */}
        <TestArtifactCard token={token} />

        {/* Supplier logo */}
        <div className="card p-6">
          <div className="section-tag mb-3">Supplier Logo</div>
          <p className="text-sm text-[var(--ink-2)] mb-4">
            Appears on the Login page and (optionally) in the quote footer.
          </p>
          <div className="flex items-center gap-5">
            <div className="w-28 h-28 border-2 border-[var(--border)] bg-[var(--bar-bg)] flex items-center justify-center overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt="Supplier logo" className="w-full h-full object-contain" data-testid="supplier-logo-preview" />
              ) : (
                <div className="font-heading text-[var(--brand-text)] text-5xl">
                  {(supplierName || "A").charAt(0)}
                </div>
              )}
            </div>
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                hidden
                onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])}
              />
              <button className="btn-primary" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="upload-supplier-logo-btn">
                <Upload className="w-4 h-4" /> {logoUrl ? "Replace" : "Upload"} Logo
              </button>
              {logoUrl && (
                <button
                  className="btn-ghost text-[var(--danger)] mt-2"
                  onClick={async () => {
                    setBusy(true);
                    try {
                      const { data } = await axios.put(
                        `${API}/admin/branding`,
                        { supplier_logo_url: "" },
                        { headers: { "X-Admin-Token": token } }
                      );
                      setBranding(data);
                      toast.success("Logo removed");
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="text-xs text-[var(--muted)] text-center pt-4">
          Bookmark this URL — it&apos;s how you&apos;ll come back to update branding.
        </div>

        {/* Pricing Tiers */}
        <PricingTiersPanel token={token} />

        {/* LP Margin Tiers — cost×margin engine ladder + per-quote picker */}
        <LpMarginTiersPanel token={token} />

        {/* Bulk pricing updates (% bump, CSV/Excel upload, export) */}
        <PricingUpdatePanel token={token} />

        {/* Mezzo Window Pricing Matrix (4 tiers × 4 product types) */}
        <MezzoPricingPanel token={token} />

        {/* Vero Window Pricing Matrix (4 tiers × 6 product types, multi-grid) */}
        <VeroPricingPanel token={token} />

        {/* ISS Siding Pricing — single-tier, CSV/Excel upload only */}
        <ISSPricingPanel token={token} />

        {/* Contractors → Tier assignment */}
        <PipelinePanel token={token} />
        <CompaniesPanel token={token} />
      </main>
    </div>
  );
}

/** LP MARGIN TIERS (iter98 — Howard approved): the cost×margin engine
 *  ladder. Percentages editable ONLY here; per-tier company counts;
 *  per-quote tier picker for LP estimates (admin boundary absolute —
 *  tier names/percentages never render on contractor surfaces). */
function LpMarginTiersPanel({ token }) {
  const [cfg, setCfg] = useState(null);
  const [pcts, setPcts] = useState({});
  const [counts, setCounts] = useState({});
  const [ests, setEsts] = useState([]);
  const [eventsFor, setEventsFor] = useState(null); // audit timeline (admin-only)
  const [busy, setBusy] = useState(false);
  const headers = { "X-Admin-Token": token };

  const load = async () => {
    const [tiers, companies, lps] = await Promise.all([
      axios.get(`${API}/admin/lp-margin-tiers`, { headers }),
      axios.get(`${API}/admin/companies`, { headers }),
      axios.get(`${API}/admin/lp-estimates`, { headers }),
    ]);
    setCfg(tiers.data);
    setPcts({ ...tiers.data.tiers });
    const c = {};
    (companies.data.companies || companies.data || []).forEach((co) => {
      if (co.tier_name) c[co.tier_name] = (c[co.tier_name] || 0) + 1;
    });
    setCounts(c);
    setEsts(lps.data.estimates || []);
  };
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [token]);

  if (!cfg) return null;
  const tierNames = Object.keys(cfg.tiers || {});
  const dirty = tierNames.some((n) => Number(pcts[n]) !== Number(cfg.tiers[n]));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/lp-margin-tiers`,
        { tiers: Object.fromEntries(tierNames.map((n) => [n, Number(pcts[n])])) },
        { headers }
      );
      setCfg(data);
      setPcts({ ...data.tiers });
      toast.success("LP margin ladder saved — reprices future quotes only");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const setTier = async (estId, tier) => {
    try {
      await axios.put(`${API}/admin/estimates/${estId}/lp-pricing-tier`,
        { tier }, { headers });
      setEsts((xs) => xs.map((e) => (e.id === estId ? { ...e, lp_pricing_tier: tier } : e)));
      toast.success(`Quote tier → ${tier}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tier change failed");
    }
  };

  return (
    <div className="card p-6" data-testid="lp-margin-tiers-card">
      <div className="section-tag mb-1">LP Margin Tiers (cost × margin engine)</div>
      <p className="text-sm text-[var(--ink-2)] mb-4">
        True margin: sell = dealer cost ÷ (1 − tier%). Editing a % reprices FUTURE
        quotes only — issued quotes keep their prices. Confidential: tiers and costs
        never render on contractor surfaces.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {tierNames.map((n) => (
          <div key={n} className="border border-[var(--border)] rounded-sm p-3" data-testid={`lp-tier-${n}`}>
            <div className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)] mb-1">
              {n}{n === cfg.default_tier ? " · default" : ""}
            </div>
            <div className="flex items-baseline gap-1">
              <input
                className="input num w-16"
                type="number" min="0" max="99" step="0.5"
                value={pcts[n] ?? ""}
                onChange={(e) => setPcts((p) => ({ ...p, [n]: e.target.value }))}
                data-testid={`lp-tier-input-${n}`}
              />
              <span className="text-sm">%</span>
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-1 font-mono-num">
              {counts[n] || 0} compan{(counts[n] || 0) === 1 ? "y" : "ies"}
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        disabled={busy || !dirty}
        className="px-5 py-2 rounded-sm text-sm font-bold uppercase tracking-wider bg-[var(--bar-bg)] text-white disabled:opacity-40"
        onClick={save}
        data-testid="lp-tiers-save"
      >
        Save ladder
      </button>

      <div className="mt-6">
        <div className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)] mb-1">
          Per-quote tier · AI extraction spend (recent LP estimates)
        </div>
        <div className="text-[11px] text-[var(--muted)] mb-2">
          Spend is live per-run telemetry (photo + blueprint extractions) — admin-only; never shown on contractor or customer surfaces.
        </div>
        {ests.length === 0 ? (
          <div className="text-sm text-[var(--muted)]">No LP estimates yet.</div>
        ) : (
          <div className="max-h-64 overflow-y-auto border border-[var(--border)] rounded-sm">
            <table className="w-full text-sm">
              <tbody>
                {ests.map((e) => (
                  <tr key={e.id} className="border-b border-[var(--border)] last:border-0" data-testid={`lp-est-row-${e.id}`}>
                    <td className="px-3 py-2 font-mono-num">{e.estimate_number || e.id.slice(0, 8)}</td>
                    <td className="px-3 py-2">{e.customer_name || "—"}</td>
                    <td className="px-3 py-2 text-[var(--muted)]">{e.company_name || ""}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-xs text-[var(--muted)]" data-testid={`lp-est-spend-${e.id}`}>
                      {e.extraction_runs
                        ? `$${Number(e.extraction_spend_usd || 0).toFixed(2)} · ${e.extraction_runs} run${e.extraction_runs === 1 ? "" : "s"}${e.extraction_untracked_runs ? ` (${e.extraction_untracked_runs} untracked)` : ""}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <select
                        className="input text-sm py-1"
                        value={e.lp_pricing_tier}
                        onChange={(ev) => setTier(e.id, ev.target.value)}
                        data-testid={`lp-est-tier-picker-${e.id}`}
                      >
                        {tierNames.map((n) => (
                          <option key={n} value={n}>{`${n} (${cfg.tiers[n]}%)`}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        className={`text-[10px] font-bold uppercase tracking-wider ${eventsFor === e.id ? "text-[var(--brand)]" : "text-[var(--ink-2)]"} underline`}
                        onClick={() => setEventsFor(eventsFor === e.id ? null : e.id)}
                        data-testid={`lp-est-events-${e.id}`}
                      >
                        Events
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {eventsFor && <AuditTimeline token={token} estId={eventsFor} />}
      </div>
    </div>
  );
}


function priceAgeDays(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function newestStamp(tier) {
  let best = null;
  for (const s of tier.sections || []) {
    for (const it of s.items || []) {
      if (it.price_changed_at && (!best || it.price_changed_at > best)) best = it.price_changed_at;
    }
  }
  return best;
}

// PRICE AGE (Howard ruled 2026-07-31): a stale price is the one defect
// that reaches a homeowner — every row shows when its price last changed,
// rows older than the (settable) threshold get the STALE chip, and rows
// with no record say so instead of faking a date.
function PriceAgeCell({ it, staleDays }) {
  const days = priceAgeDays(it.price_changed_at);
  if (days === null) {
    return (
      <span className="text-[9px] uppercase tracking-wider text-[var(--muted)]" data-testid={`price-age-none-${it.name}`}>
        no age record
      </span>
    );
  }
  const stale = days > staleDays;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold ${stale ? "text-amber-700" : "text-[var(--ink-2)]"}`}
      title={`Last changed ${new Date(it.price_changed_at).toLocaleString()} by ${it.price_changed_by || "?"}`}
      data-testid={`price-age-${it.name}`}
    >
      {new Date(it.price_changed_at).toLocaleDateString()}
      {stale && (
        <span className="px-1 py-0.5 border border-amber-400 bg-amber-50" data-testid={`price-stale-chip-${it.name}`}>
          STALE {days}d
        </span>
      )}
    </span>
  );
}

function PricingTiersPanel({ token }) {
  const [tiers, setTiers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editTier, setEditTier] = useState(null);
  const [busy, setBusy] = useState(false);
  const [staleDays, setStaleDays] = useState(90);

  const load = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/tiers`, { headers: { "X-Admin-Token": token } });
      setTiers(data);
      const b = await axios.get(`${API}/branding`);
      if (b.data?.stale_price_days) setStaleDays(b.data.stale_price_days);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }, [token]);

  const saveStaleDays = async (v) => {
    const n = Number(v) || 90;
    setStaleDays(n);
    try {
      await axios.put(`${API}/admin/branding`, { stale_price_days: n }, { headers: { "X-Admin-Token": token } });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = async (tierId) => {
    setEditingId(tierId);
    const { data } = await axios.get(`${API}/admin/tiers/${tierId}`, { headers: { "X-Admin-Token": token } });
    setEditTier(data);
  };

  const updateItem = (si, ii, key, val) => {
    setEditTier((t) => {
      const next = JSON.parse(JSON.stringify(t));
      next.sections[si].items[ii][key] = key === "name" || key === "unit" ? val : Number(val) || 0;
      return next;
    });
  };

  const saveTier = async (confirmMagnitude = false) => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/tiers/${editingId}`, {
        sections: editTier.sections,
        confirm_magnitude: confirmMagnitude === true,
      }, { headers: { "X-Admin-Token": token } });
      toast.success(`${editTier.name} prices saved`);
      await load();
    } catch (e) {
      // TRANSPOSITION GATE (ruled 2026-07-31): a price moving past ×3
      // never saves without an explicit human confirm.
      const gate = e.response?.status === 409 ? e.response?.data?.detail?.magnitude_gate : null;
      if (gate && confirmMagnitude !== true) {
        const rows = gate.map((g) =>
          `${g.name} (${g.field}): $${Number(g.old).toFixed(2)} → $${Number(g.new).toFixed(2)} (${g.pct > 0 ? "+" : ""}${g.pct}%)`
        ).join("\n");
        setBusy(false);
        if (window.confirm(`⚠ PRICE MOVES MORE THAN ×3 — real swing, or a crossed/transposed entry?\n\n${rows}\n\nSave anyway?`)) {
          return saveTier(true);
        }
        return;
      }
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-6 mt-6" data-testid="tiers-panel">
      <div className="flex items-center gap-3 mb-4">
        <Tags className="w-5 h-5 text-[var(--brand-text)]" />
        <div className="section-tag">Pricing Tiers</div>
      </div>
      <p className="text-sm text-[var(--ink-2)] mb-4">
        Material prices each contractor sees, by tier. Labor numbers shown here are the
        defaults — contractors can override labor per estimate. Click a tier to edit prices.
      </p>
      <div className="flex items-center gap-2 mb-4" data-testid="stale-threshold-row">
        <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--muted)]">
          Flag prices older than
        </span>
        <input
          className="input num h-8 text-sm w-20"
          type="number"
          min="1"
          max="3650"
          value={staleDays}
          onChange={(e) => setStaleDays(Number(e.target.value) || 0)}
          onBlur={(e) => saveStaleDays(e.target.value)}
          data-testid="stale-days-input"
        />
        <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--muted)]">days</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {tiers.map((t) => (
          <button
            key={t.id}
            className={`p-4 border-2 text-left transition-all ${editingId === t.id ? "border-[var(--brand)] bg-orange-50" : "border-[var(--border)] hover:border-[var(--border-strong)]"}`}
            onClick={() => openEdit(t.id)}
            data-testid={`tier-${t.name}`}
          >
            <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">Tier</div>
            <div className="font-heading text-lg text-[var(--ink)]">{t.name}</div>
            <div className="text-xs text-[var(--ink-2)] mt-1">
              {(t.sections || []).reduce((s, x) => s + (x.items || []).length, 0)} items
            </div>
            <div className="text-[10px] text-[var(--muted)] mt-1" data-testid={`tier-age-${t.name}`}>
              {newestStamp(t)
                ? `last price change ${new Date(newestStamp(t)).toLocaleDateString()}`
                : "no price-change record yet"}
            </div>
          </button>
        ))}
      </div>

      {editTier && (
        <div className="border-t border-[var(--border)] pt-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-xl">{editTier.name}</h3>
            <button className="btn-primary" onClick={() => saveTier()} disabled={busy} data-testid="save-tier-btn">
              {busy ? "Saving…" : "Save Tier"}
            </button>
          </div>
          {editTier.sections.map((s) => {
            const si = editTier.sections.indexOf(s);
            return (
              <div key={s.title} className="mb-4 border border-[var(--border)]">
                <div className="bg-[var(--surface-muted)] px-3 py-2 text-xs uppercase tracking-wider font-bold text-[var(--ink-2)]">
                  {s.title}
                </div>
                {s.items.map((it) => {
                  const ii = s.items.indexOf(it);
                  return (
                    <div key={it.name} className="grid grid-cols-12 gap-2 px-3 py-1 border-t border-[var(--border)] items-center">
                      <div className="col-span-4 text-sm">{it.name}</div>
                      <div className="col-span-1 text-[10px] text-[var(--muted)] uppercase">{it.unit}</div>
                      <div className="col-span-2">
                        <input
                          className="input num h-8 text-sm"
                          type="number"
                          step="0.01"
                          value={it.mat}
                          onChange={(e) => updateItem(si, ii, "mat", e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          className="input num h-8 text-sm"
                          type="number"
                          step="0.01"
                          value={it.lab}
                          onChange={(e) => updateItem(si, ii, "lab", e.target.value)}
                        />
                      </div>
                      <div className="col-span-3 text-right">
                        <PriceAgeCell it={it} staleDays={staleDays} />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function PipelinePanel({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/pipeline`,
          { headers: { "X-Admin-Token": token } }
        );
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || e.message);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (error) {
    return (
      <div className="card p-6 mt-6">
        <div className="text-sm text-[var(--danger-text)]">Could not load pipeline: {error}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="card p-6 mt-6">
        <div className="text-sm text-[var(--muted)]">Loading pipeline…</div>
      </div>
    );
  }

  const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
  const winRate = data.win_rate == null ? "—" : `${data.win_rate}%`;

  // Top performers — companies with the most won_dollars
  const topByWon = Object.entries(data.by_company || {})
    .map(([id, row]) => ({ id, ...row }))
    .filter((r) => r.accepted > 0 || r.sent > 0)
    .sort((a, b) => (b.won_dollars - a.won_dollars) || (b.accepted - a.accepted))
    .slice(0, 5);

  return (
    <div className="card p-6 mt-6" data-testid="pipeline-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="section-tag">Pipeline · All Contractors</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <PipelineStat label="Total Estimates" value={data.total_estimates} />
        <PipelineStat label="Drafts" value={data.drafts} />
        <PipelineStat label="Sent" value={data.sent} sublabel={`${usd(data.pending_dollars)} pending`} accent="orange" />
        <PipelineStat label="Accepted" value={data.accepted} sublabel={`${usd(data.won_dollars)} won`} accent="green" />
        <PipelineStat
          label="Win Rate"
          value={winRate}
          sublabel={
            data.sent + data.accepted === 0
              ? "no quotes yet"
              : `${data.accepted} of ${data.sent + data.accepted} sent`
          }
        />
      </div>

      {topByWon.length > 0 && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-bold mb-2">
            Top contractors by won revenue
          </div>
          <div className="border border-[var(--border)]">
            <div className="grid grid-cols-12 gap-3 px-3 py-2 text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold bg-[var(--surface-muted)]">
              <div className="col-span-5">Contractor</div>
              <div className="col-span-2 text-right">Sent</div>
              <div className="col-span-2 text-right">Accepted</div>
              <div className="col-span-3 text-right">Won $</div>
            </div>
            {topByWon.map((c) => (
              <div key={c.id} className="grid grid-cols-12 gap-3 px-3 py-2 border-t border-[var(--border)] items-center text-sm">
                <div className="col-span-5 font-semibold text-[var(--ink)] truncate">{c.name}</div>
                <div className="col-span-2 text-right font-mono-num text-[var(--ink-2)]">{c.sent}</div>
                <div className="col-span-2 text-right font-mono-num text-[#15803D] font-bold">{c.accepted}</div>
                <div className="col-span-3 text-right font-mono-num text-[var(--ink)] font-bold">{usd(c.won_dollars)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PipelineStat({ label, value, sublabel, accent }) {
  const stripe =
    accent === "orange" ? "bg-[var(--brand)]"
      : accent === "green" ? "bg-[var(--success)]"
      : "bg-[var(--table-header)]";
  return (
    <div className="flex border border-[var(--border)]">
      <div className={`w-1 ${stripe}`} />
      <div className="px-3 py-2 flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-bold">{label}</div>
        <div className="font-mono-num text-2xl font-bold text-[var(--ink)] leading-tight">{value}</div>
        {sublabel ? <div className="text-[11px] text-[var(--muted)] truncate">{sublabel}</div> : null}
      </div>
    </div>
  );
}

function InviteContractorPanel({ token, signupCode }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/invitations`, { headers: { "X-Admin-Token": token } });
      setInvitations(data);
      setLoaded(true);
    } catch (e) {
      // Non-fatal — sending still works even if the history list fails to load.
      console.warn("Failed to load invitations", e);
      setLoaded(true);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e?.preventDefault?.();
    const clean = email.trim();
    if (!clean) {
      toast.error("Enter an email address first.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/invite-contractor`, {
        email: clean,
        name: name.trim() || undefined,
        personal_note: note.trim() || undefined,
        app_url: window.location.origin,
      }, { headers: { "X-Admin-Token": token } });
      toast.success(`Invitation sent to ${clean}`);
      setEmail("");
      setName("");
      setNote("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to send invite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-6" data-testid="invite-contractor-panel">
      <div className="flex items-center gap-3 mb-3">
        <Mail className="w-5 h-5 text-[var(--brand-text)]" />
        <div className="section-tag">Invite a Contractor by Email</div>
      </div>
      <p className="text-sm text-[var(--ink-2)] mb-4">
        We&apos;ll send a branded email with a one-click signup link (your access code{" "}
        <span className="font-mono-num text-[var(--ink)]">{signupCode}</span> is pre-filled).
      </p>
      <form onSubmit={send} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label">Email *</label>
            <input
              type="email"
              className="input"
              placeholder="contractor@example.com"
              value={email}
              required
              onChange={(ev) => setEmail(ev.target.value)}
              data-testid="invite-email-input"
            />
          </div>
          <div>
            <label className="label">Contractor name (optional)</label>
            <input
              className="input"
              placeholder="e.g. Bob Smith"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              data-testid="invite-name-input"
            />
          </div>
        </div>
        <div>
          <label className="label">Personal note (optional)</label>
          <textarea
            className="input"
            rows={2}
            maxLength={500}
            placeholder="Hey Bob — wanted to get you onto our new quoting tool. Tier 2 pricing is loaded for your account."
            value={note}
            onChange={(ev) => setNote(ev.target.value)}
            data-testid="invite-note-input"
          />
        </div>
        <button
          type="submit"
          className="btn-primary"
          disabled={busy}
          data-testid="invite-send-btn"
        >
          <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send Invitation"}
        </button>
      </form>

      {loaded && invitations.length > 0 && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-bold mb-2">
            Recent invitations ({invitations.length})
          </div>
          <div className="border border-[var(--border)] max-h-72 overflow-y-auto" data-testid="invitations-list">
            <div className="grid grid-cols-12 gap-3 px-3 py-2 text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold bg-[var(--surface-muted)] sticky top-0">
              <div className="col-span-5">Email</div>
              <div className="col-span-3">Sent</div>
              <div className="col-span-2 text-right">Status</div>
              <div className="col-span-2 text-right">Link</div>
            </div>
            {invitations.map((inv) => (
              <div
                key={inv.id}
                className="grid grid-cols-12 gap-3 px-3 py-2 border-t border-[var(--border)] items-center text-sm"
                data-testid={`invitation-row-${inv.email}`}
              >
                <div className="col-span-5 truncate">
                  <div className="font-semibold text-[var(--ink)] truncate">{inv.email}</div>
                  {inv.name && <div className="text-[10px] text-[var(--muted)] truncate">{inv.name}</div>}
                </div>
                <div className="col-span-3 text-xs text-[var(--ink-2)]">
                  {new Date(inv.sent_at).toLocaleString()}
                </div>
                <div className="col-span-2 text-right">
                  {inv.registered ? (
                    <span className="inline-block px-2 py-0.5 bg-[#DCFCE7] text-[#15803D] text-[10px] uppercase tracking-wider font-bold">
                      Signed up
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 bg-[#FEF3C7] text-[var(--warning-text)] text-[10px] uppercase tracking-wider font-bold">
                      Pending
                    </span>
                  )}
                </div>
                <div className="col-span-2 text-right">
                  <button
                    type="button"
                    className="text-[10px] text-[var(--ink-2)] hover:text-[var(--brand-text)] underline"
                    onClick={() => {
                      navigator.clipboard.writeText(inv.register_url);
                      toast.success("Signup link copied");
                    }}
                    title={inv.register_url}
                  >
                    Copy link
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function CompaniesPanel({ token }) {
  const [companies, setCompanies] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState({});

  const load = React.useCallback(async () => {
    try {
      const [co, t] = await Promise.all([
        axios.get(`${API}/admin/companies`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/admin/tiers`, { headers: { "X-Admin-Token": token } }),
      ]);
      setCompanies(co.data);
      setTiers(t.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }, [token]);
  useEffect(() => {
    load();
  }, [load]);

  const assign = async (companyId, tierId) => {
    setBusy((b) => ({ ...b, [companyId]: true }));
    try {
      await axios.put(`${API}/admin/companies/${companyId}/tier`, {
        price_tier_id: tierId,
      }, { headers: { "X-Admin-Token": token } });
      toast.success("Tier updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy((b) => ({ ...b, [companyId]: false }));
    }
  };

  const remove = async (company) => {
    const typed = window.prompt(
      `This will permanently delete "${company.name}", its ${company.estimate_count} estimate(s), and all of its team accounts.\n\n` +
      `Type the company name exactly to confirm:`
    );
    if (typed === null) return; // user cancelled
    if (typed.trim() !== company.name) {
      toast.error("Name did not match. Nothing was deleted.");
      return;
    }
    setBusy((b) => ({ ...b, [company.id]: true }));
    try {
      const { data } = await axios.delete(
        `${API}/admin/companies/${company.id}`,
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(
        `Deleted ${data.company_name} (${data.estimates_deleted} estimates, ${data.users_deleted} users)`
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy((b) => ({ ...b, [company.id]: false }));
    }
  };

  return (
    <div className="card p-6 mt-6" data-testid="companies-panel">
      <div className="flex items-center gap-3 mb-4">
        <Building2 className="w-5 h-5 text-[var(--brand-text)]" />
        <div className="section-tag">Contractor Companies ({companies.length})</div>
      </div>
      <p className="text-sm text-[var(--ink-2)] mb-4">
        Assign each contractor to a pricing tier. Changing a tier takes effect on their
        next estimate. Existing saved estimates keep their original prices.
      </p>
      <div className="border border-[var(--border)] max-h-[500px] overflow-y-auto">
        <div className="grid grid-cols-12 gap-3 px-3 py-2 text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold bg-[var(--surface-muted)] sticky top-0">
          <div className="col-span-4">Company</div>
          <div className="col-span-3">Tier</div>
          <div className="col-span-2 text-right">Estimates</div>
          <div className="col-span-2 text-right">Created</div>
          <div className="col-span-1 text-right">&nbsp;</div>
        </div>
        {companies.map((c) => (
          <div key={c.id} className="grid grid-cols-12 gap-3 px-3 py-2 border-t border-[var(--border)] items-center text-sm">
            <div className="col-span-4">
              <div className="font-semibold text-[var(--ink)]">{c.name}</div>
              <div className="text-[10px] text-[var(--muted)] font-mono-num">{c.invite_code}</div>
            </div>
            <div className="col-span-3">
              <select
                className="input h-9 text-sm"
                value={c.price_tier_id || ""}
                onChange={(e) => assign(c.id, e.target.value)}
                disabled={busy[c.id]}
                data-testid={`tier-select-${c.id}`}
              >
                {tiers.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2 text-right font-mono-num">{c.estimate_count}</div>
            <div className="col-span-2 text-right text-xs text-[var(--muted)]">
              {new Date(c.created_at).toLocaleDateString()}
            </div>
            <div className="col-span-1 text-right">
              <button
                type="button"
                onClick={() => remove(c)}
                disabled={busy[c.id]}
                className="p-1.5 text-[var(--muted)] hover:text-[var(--danger-text)] hover:bg-[var(--danger-soft)] rounded-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                title={`Delete ${c.name}`}
                data-testid={`delete-company-${c.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Audit timeline (approved 2026-07-15): one-page, per-estimate,
// admin-boundary, READ-ONLY — the full event stream (customer journey +
// ratify verbs) with by/at. Never proxied to contractor surfaces.
function AuditTimeline({ token, estId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setData(null);
    setErr(null);
    axios.get(`${API}/admin/estimates/${estId}/events`, { headers: { "X-Admin-Token": token } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [estId, token]);
  if (err) return <div className="mt-2 text-xs text-red-700" data-testid="audit-timeline-error">{err}</div>;
  if (!data) return <div className="mt-2 text-xs text-[var(--muted)]">Loading events…</div>;
  return (
    <div className="mt-2 border border-[var(--border)] rounded-sm bg-[var(--surface)]" data-testid="audit-timeline">
      <div className="px-3 py-1.5 border-b border-[var(--border)] text-[10px] font-bold uppercase tracking-wider text-[var(--ink-2)] flex justify-between">
        <span>Audit timeline — {data.estimate?.customer_name || data.estimate?.estimate_number || estId.slice(0, 8)}</span>
        <span className="font-mono-num">{data.total} event{data.total === 1 ? "" : "s"}</span>
      </div>
      {data.total === 0 ? (
        <div className="px-3 py-2 text-xs text-[var(--muted)]" data-testid="audit-timeline-empty">No events recorded yet.</div>
      ) : (
        <div className="max-h-56 overflow-y-auto">
          {data.events.map((ev, i) => (
            <div key={i} className="px-3 py-1.5 border-b border-[var(--border)] last:border-0 flex flex-wrap items-baseline gap-x-3 text-xs" data-testid={`audit-event-${i}`}>
              <span className="font-mono text-[10px] text-[var(--muted)] whitespace-nowrap">
                {ev.at ? new Date(ev.at).toLocaleString() : "—"}
              </span>
              <span className="font-bold text-[var(--ink)]">{ev.type}</span>
              {ev.meta?.by && <span className="text-[var(--muted)]">by {ev.meta.by}</span>}
              {ev.meta?.code && <span className="text-[var(--muted)]">flag: {ev.meta.code}</span>}
              {ev.meta?.values?.wall_heights_ft && (
                <span className="font-mono text-[10px] text-[var(--muted)]">
                  heights: {ev.meta.values.wall_heights_ft.join(", ")} ft
                </span>
              )}
              {ev.meta?.from && <span className="text-[var(--muted)]">from {Array.isArray(ev.meta.from) ? ev.meta.from.join("/") : ev.meta.from}</span>}
              {ev.meta?.to && <span className="text-[var(--muted)]">→ {ev.meta.to}</span>}
              {ev.meta?.key && <span className="font-mono text-[10px] text-[var(--muted)] truncate max-w-[280px]">{ev.meta.key}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
